import React from 'react';

import { Collapsible, CollapsibleHeaderContent, SpellName } from '@dndbeyond/character-components/es';
import {
    Hack__BaseCharClass,
    CharacterTheme,
    ClassUtils,
    DataOriginRefData,
    EntityValueLookup,
    RuleData,
    Spell,
    SpellCasterInfo,
    SpellUtils,
} from '@dndbeyond/character-rules-engine/es';

import SpellDetail from '../../SpellDetail';
import { RemoveButton, ThemeButton } from '../../common/Button';

interface Props {
    spell: Spell;
    buttonUnprepareText: string;
    buttonActiveStyle: string;
    enableSpellRemove: boolean;
    isPrepareMaxed: boolean;
    isCantripsKnownMaxed: boolean;
    isSpellsKnownMaxed: boolean;
    addButtonText: string;
    enableAdd: boolean;
    enablePrepare: boolean;
    enableUnprepare: boolean;
    showExpandedType: boolean;
    showCustomize: boolean;
    onPrepare: (spell: Spell) => void;
    onUnprepare: (spell: Spell) => void;
    onRemove: (spell: Spell) => void;
    onAdd: (spell: Spell) => void;
    spellCasterInfo: SpellCasterInfo;
    ruleData: RuleData;
    entityValueLookup: EntityValueLookup;
    dataOriginRefData: DataOriginRefData;
    proficiencyBonus: number;
    theme: CharacterTheme;
}
export default class SpellManagerItem extends React.PureComponent<Props> {
    static defaultProps = {
        enableSpellRemove: true,
        enableAdd: true,
        enablePrepare: true,
        enableUnprepare: true,
        buttonUnprepareText: 'Prepared',
        buttonActiveStyle: '',
    };

    handlePrepareToggle = (): void => {
        const { spell, onPrepare, onUnprepare } = this.props;

        if (SpellUtils.isPrepared(spell)) {
            onUnprepare(spell);
        } else {
            onPrepare(spell);
        }
    };

    handleRemove = (): void => {
        const { spell, onRemove } = this.props;

        onRemove(spell);
    };

    handleAdd = (): void => {
        const { spell, onAdd } = this.props;

        onAdd(spell);
    };

    renderButtons = (classNames: Array<string>): React.ReactNode => {
        const {
            spell,
            isPrepareMaxed,
            isCantripsKnownMaxed,
            isSpellsKnownMaxed,
            addButtonText,
            enableSpellRemove,
            enableAdd,
            enablePrepare,
            enableUnprepare,
            buttonUnprepareText,
            buttonActiveStyle,
        } = this.props;

        const alwaysPrepared = SpellUtils.isAlwaysPrepared(spell);
        const isPrepared = SpellUtils.isPrepared(spell);
        const canRemove = SpellUtils.canRemove(spell);
        const canPrepare = SpellUtils.canPrepare(spell);
        const canAdd = SpellUtils.canAdd(spell);
        const dataOrigin = SpellUtils.getDataOrigin(spell);

        let isAddDisabled: boolean = false;
        if (SpellUtils.isCantrip(spell) && isCantripsKnownMaxed) {
            isAddDisabled = true;
        }
        if (!SpellUtils.isCantrip(spell) && isSpellsKnownMaxed) {
            isAddDisabled = true;
        }

        let showPrepare: boolean = !alwaysPrepared && canPrepare && (enablePrepare || enableUnprepare);

        let showRemove: boolean =
            canRemove &&
            (enableSpellRemove ||
                (ClassUtils.isSpellbookSpellcaster(dataOrigin.primary as Hack__BaseCharClass) &&
                    SpellUtils.isCantrip(spell)));

        return (
            <div className={classNames.join(' ')}>
                {alwaysPrepared && <div className="ct-spell-manager__spell-always">Always Prepared</div>}
                {showPrepare && (
                    <ThemeButton
                        size="small"
                        disabled={isPrepareMaxed && !isPrepared}
                        style={isPrepared ? buttonActiveStyle : 'outline'}
                        onClick={this.handlePrepareToggle}
                        stopPropagation={true}
                    >
                        {isPrepared ? buttonUnprepareText : 'Prepare'}
                    </ThemeButton>
                )}
                {showRemove && <RemoveButton onClick={this.handleRemove} style={buttonActiveStyle} />}
                {enableAdd && canAdd && (
                    <ThemeButton
                        size="small"
                        onClick={this.handleAdd}
                        disabled={isAddDisabled}
                        style={'outline'}
                        stopPropagation={true}
                    >
                        {addButtonText}
                    </ThemeButton>
                )}
            </div>
        );
    };

    renderHeader = (): React.ReactNode => {
        const { spell, showExpandedType, dataOriginRefData, theme } = this.props;

        return (
            <CollapsibleHeaderContent
                heading={
                    <SpellName
                        theme={theme}
                        spell={spell}
                        showExpandedType={showExpandedType}
                        dataOriginRefData={dataOriginRefData}
                    />
                }
                callout={this.renderButtons(['ct-spell-manager__spell-header-actions'])}
            />
        );
    };

    render() {
        const {
            spell,
            spellCasterInfo,
            ruleData,
            entityValueLookup,
            isPrepareMaxed,
            showCustomize,
            proficiencyBonus,
            theme,
        } = this.props;

        return (
            <Collapsible layoutType={'minimal'} header={this.renderHeader()} className="ct-spell-manager__spell">
                <SpellDetail
                    theme={theme}
                    spell={spell}
                    isPreparedMaxed={isPrepareMaxed}
                    enableCaster={false}
                    spellCasterInfo={spellCasterInfo}
                    ruleData={ruleData}
                    onPrepareToggle={this.handlePrepareToggle}
                    onRemove={this.handleRemove}
                    entityValueLookup={entityValueLookup}
                    showCustomize={showCustomize}
                    proficiencyBonus={proficiencyBonus}
                />
            </Collapsible>
        );
    }
}
